import { AutoModeOutlined, DescriptionOutlined, Fingerprint, Send, MarkEmailReadOutlined } from "@mui/icons-material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
    Box,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
} from "@mui/material";
import React, { useState } from "react";
import ModalUpdateStatusProposal from "../Modals/ModalUpdateStatusProposal";
import { useProfile } from "../../../../../context/ProfileContext";
import { FinancialService } from "../../../../../shared/services/api/financial/FinancialService";
import { ProposalService } from "../../../../../shared/services/api/proposal/ProposalService";
import { ToastContent } from "../../../../../shared/components";
import { useToast } from "../../../../../shared/hooks";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../../../shared/enums/profiles";
import { returnMessageError } from "../../../../../shared/utils/returnMessageError";
import { ModalDetailBiometrics } from "../../../../analysis/components/Modals/ModaDetailBiometrics";

interface ProposalActionsProps {
    rows: any;
    onUpdate: () => void;
}

const ProposalActions: React.FC<ProposalActionsProps> = ({
    rows,
    onUpdate,
}) => {
    const { isSuperAdmin, permissions, isPermissionInitialized } = useProfile();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isUpdateStatus, setIsUpdateStatus] = useState(false);
    const [isBiometryModalOpen, setIsBiometryModalOpen] = useState(false);
    const toastHandler = useToast();
    const canViewBiometry =
        isPermissionInitialized &&
        (isSuperAdmin || !!permissions?.[PermissionRolesEnum.WarrantiesOptions]?.[PermissionActionsEnum.ViewWarrantyBiometry]);
    const canViewSignedWarrantyContract =
        isPermissionInitialized &&
        !!permissions?.[PermissionRolesEnum.WarrantiesOptions]?.[PermissionActionsEnum.ViewSignedWarrantyContract];
    const showViewBiometry = Boolean(canViewBiometry && rows?.analysisId);
    const showViewSignedWarrantyContract = Boolean(canViewSignedWarrantyContract && rows?.id);
    const canAlterStatus =
        isPermissionInitialized &&
        (isSuperAdmin || !!permissions?.[PermissionRolesEnum.WarrantiesOptions]?.[PermissionActionsEnum.AlterStatus]);
    const canResendBiometryLink =
        isPermissionInitialized &&
        (isSuperAdmin || !!permissions?.[PermissionRolesEnum.AnalysisOptions]?.[PermissionActionsEnum.Biometria]);
    const canSendPartnerCertificate =
        isPermissionInitialized &&
        (isSuperAdmin || !!permissions?.[PermissionRolesEnum.WarrantiesOptions]?.[PermissionActionsEnum.SendPartnerCertificate]);

    // Abre o menu
    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    // Fecha o menu
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    // Fecha o modal e notifica o componente pai para atualizar os dados
    const handleCloseModalUpdateStatus = () => {
        setIsModalOpen(false);
        onUpdate(); // ✅ Chama a função do componente pai para atualização
    };

    // Abre o modal e fecha o menu antes
    const handleOpenModal = () => {
        handleMenuClose();
        setTimeout(() => {
            setIsModalOpen(true);
        }, 100);
    };

    const handleResendInstallment = (id: string, email: string) => {
        FinancialService.sendMailSetup(id, { email }).then((result) => {
            if (result instanceof Error) {
                toastHandler.present({
                    type: "error",
                    position: "top-right",
                    messageOrContent: (
                        <ToastContent
                            title={"Erro"}
                            content={"Erro ao enviar email"}
                        />
                    ),
                });
            } else {
                toastHandler.present({
                    type: "success",
                    position: "top-right",
                    messageOrContent: (
                        <ToastContent
                            title={"Sucesso"}
                            content={"Email enviado com sucesso !!!"}
                        />
                    ),
                });
            }
        });
    };

    const handleSendPartnerCertificate = async (id: string) => {
        handleMenuClose();

        const result = await ProposalService.sendCertificatePartnerWebhook(id);
        if (result instanceof Error) {
            toastHandler.present({
                type: "error",
                position: "top-right",
                messageOrContent: (
                    <ToastContent
                        title={"Erro"}
                        content={"Erro ao enviar certificado para o parceiro"}
                    />
                ),
            });
        } else {
            toastHandler.present({
                type: "success",
                position: "top-right",
                messageOrContent: (
                    <ToastContent
                        title={"Sucesso"}
                        content={"Certificado enviado ao parceiro com sucesso !!!"}
                    />
                ),
            });
        }
    };

    const handleOpenBiometry = () => {
        handleMenuClose();

        if (!canViewBiometry) {
            toastHandler.present({
                type: "error",
                position: "top-right",
                durationMs: 4000,
                messageOrContent: <ToastContent title="Permissão negada" content="Você não possui permissão para visualizar a biometria." />,
            });
            return;
        }

        if (!rows?.analysisId) {
            toastHandler.present({
                type: "error",
                position: "top-right",
                durationMs: 4000,
                messageOrContent: <ToastContent title="Biometria indisponível" content="Esta garantia não possui análise vinculada para consulta de biometria." />,
            });
            return;
        }

        setIsBiometryModalOpen(true);
    };

    const handleViewSignedWarrantyContract = async () => {
        handleMenuClose();

        if (!canViewSignedWarrantyContract) {
            toastHandler.present({
                type: "error",
                position: "top-right",
                durationMs: 4000,
                messageOrContent: <ToastContent title="Permissão negada" content="Você não possui permissão para visualizar o contrato assinado." />,
            });
            return;
        }

        if (!rows?.id) {
            toastHandler.present({
                type: "error",
                position: "top-right",
                durationMs: 4000,
                messageOrContent: <ToastContent title="Contrato indisponível" content="Esta garantia não possui identificador válido para consulta do contrato assinado." />,
            });
            return;
        }

        try {
            const result = await ProposalService.getSignedWarrantyContract(rows.id);
            if (result instanceof Error) {
                throw result;
            }
        } catch (error) {
            const { titleMessage, contentMessage } = returnMessageError(error);
            toastHandler.present({
                type: "error",
                position: "top-right",
                durationMs: 4000,
                messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
            });
        }
    };

    return (
        <>
            <Box>
                <IconButton
                    onClick={handleMenuOpen}
                    size="small"
                    aria-controls={anchorEl ? "proposal-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={!!anchorEl}
                >
                    <MoreVertIcon />
                </IconButton>
            </Box>

            <Menu
                anchorEl={anchorEl}
                id="proposal-menu"
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
                {canAlterStatus && (
                    <MenuItem onClick={handleOpenModal}>
                        <ListItemIcon>
                            <AutoModeOutlined fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Alterar Status</ListItemText>
                    </MenuItem>
                )}
                {showViewBiometry && (
                    <MenuItem onClick={handleOpenBiometry}>
                        <ListItemIcon>
                            <Fingerprint fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Visualizar Biometria</ListItemText>
                    </MenuItem>
                )}
                {showViewSignedWarrantyContract && (
                    <MenuItem onClick={handleViewSignedWarrantyContract}>
                        <ListItemIcon>
                            <DescriptionOutlined fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Visualizar Contrato Assinado</ListItemText>
                    </MenuItem>
                )}
                {canResendBiometryLink && (
                    <MenuItem onClick={() => handleResendInstallment(rows.id, rows.email)}>
                        <ListItemIcon>
                            <Send fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Reenviar Link de Biometria</ListItemText>
                    </MenuItem>
                )}
                {canSendPartnerCertificate && (
                    <MenuItem onClick={() => handleSendPartnerCertificate(rows.id)}>
                        <ListItemIcon>
                            <MarkEmailReadOutlined fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Enviar Certificado Parceiro</ListItemText>
                    </MenuItem>
                )}
            </Menu>

            {isModalOpen && (
                <ModalUpdateStatusProposal
                    rows={rows}
                    isClose={handleCloseModalUpdateStatus}
                    isOpenModal={isModalOpen}
                    isUpdate={isUpdateStatus}
                />
            )}

            {isBiometryModalOpen && rows?.analysisId && (
                <ModalDetailBiometrics
                    isOpen={isBiometryModalOpen}
                    onClose={() => setIsBiometryModalOpen(false)}
                    biometricsId={rows.analysisId}
                />
            )}
        </>
    );
};

export default ProposalActions;
